import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    duration_months: "",
    price: "",
    currency: "INR",
    description: "",
    role_codes: [],
  });

  const roleOptions = ["SELLER", "BUYER"];
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        API_ENDPOINTS.SUBSCRIPTIONS.PLANS_LIST
      );
      if (response) {
        setPlans(response || []);
      } else {
        toast.error(response.message || "Failed to fetch subscription plans");
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      toast.error("Failed to fetch subscription plans");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = e.target.checked;
      setFormData((prev) => ({
        ...prev,
        role_codes: checked
          ? [...prev.role_codes, value]
          : prev.role_codes.filter((role) => role !== value),
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };
  const openAddModal = () => {
    setModalMode("add");
    setFormData({
      name: "",
      duration_months: "",
      price: "",
      currency: "INR",
      description: "",
      role_codes: [],
    });
    setEditingPlan(null);
    setShowModal(true);
  };

  const openEditModal = (plan) => {
    setModalMode("edit");
    setFormData({
      name: plan.name || "",
      duration_months: plan.duration_months || "",
      price: plan.price || "",
      currency: plan.currency || "INR",
      description: plan.description || "",
      role_codes: plan.roles ? plan.roles.map((role) => role.code) : [],
    });
    setEditingPlan(plan);
    setShowModal(true);
  };

  const openDeleteModal = (plan) => {
    setDeletingPlan(plan);
    setDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!formData.duration_months || formData.duration_months <= 0) {
      toast.error("Duration in months is required and must be greater than 0");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Price is required and must be greater than 0");
      return;
    }

    if (formData.role_codes.length === 0) {
      toast.error("At least one role must be selected");
      return;
    }

    try {
      if (modalMode === "add") {
        const response = await apiService.post(
          API_ENDPOINTS.SUBSCRIPTIONS.PLANS_CREATE,
          formData
        );
        if (response.id) {
          toast.success(
            response.message || "Subscription plan created successfully"
          );
          setShowModal(false);
          fetchPlans();
        } else {
          toast.error(response.message || "Failed to create subscription plan");
        }
      } else {
        const response = await apiService.put(
          API_ENDPOINTS.SUBSCRIPTIONS.PLANS_UPDATE(editingPlan.id),
          formData
        );
        if (response) {
          toast.success(
            response.message || "Subscription plan updated successfully"
          );
          setShowModal(false);
          fetchPlans();
        } else {
          toast.error(response.message || "Failed to update subscription plan");
        }
      }
    } catch (error) {
      console.error("Error saving subscription plan:", error);
      toast.error("Failed to save subscription plan");
    }
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;
    try {
      await apiService.delete(
        API_ENDPOINTS.SUBSCRIPTIONS.PLANS_DELETE(deletingPlan.id)
      );
      toast.success("Subscription plan deleted successfully");
      setDeleteModal(false);
      setDeletingPlan(null);
      fetchPlans();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to delete subscription plan");
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      name: "",
      duration_months: "",
      price: "",
      currency: "INR",
      description: "",
      role_codes: [],
    });
    setEditingPlan(null);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingPlan(null);
  };

  const formatPrice = (price, currency) => {
    return `${currency} ${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (months) => {
    if (months === 1) return "1 Month";
    return `${months} Months`;
  };
  
  useEffect(() => {
    fetchPlans();
  }, []);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Subscription Plans</h3>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="fe fe-plus"></i> Add New Plan
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Subscription</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Name</th>
                        <th>Duration</th>
                        <th>Price</th>
                        <th>Roles</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No subscription plans found
                          </td>
                        </tr>
                      ) : (
                        plans.map((plan,index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{plan.name}</strong>
                            </td>
                            <td>{formatDuration(plan.duration_months)}</td>
                            <td>
                              <span className="badge bg-success">
                                {formatPrice(plan.price, plan.currency)}
                              </span>
                            </td>
                            <td>
                              {plan.roles &&
                                plan.roles.map((role, index) => (
                                  <span
                                    key={index}
                                    className="badge bg-info me-1"
                                  >
                                    {role.name}
                                  </span>
                                ))}
                            </td>
                            <td>
                              <span className="">
                                {plan.description
                                  ? plan.description.length > 50
                                    ? `${plan.description.substring(0, 50)}...`
                                    : plan.description
                                  : "No description"}
                              </span>
                            </td>
                            <td>
                              <div className="actions">
                                <button
                                  className="btn btn-sm btn-outline-primary me-2"
                                  onClick={() => openEditModal(plan)}
                                  title="Edit"
                                >
                                  <i className="fe fe-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openDeleteModal(plan)}
                                  title="Delete"
                                >
                                  <i className="fe fe-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "add"
                    ? "Add New Subscription Plan"
                    : "Edit Subscription Plan"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Plan Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="e.g., Monthly Seller Plan"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="duration_months" className="form-label">
                          Duration (Months){" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="duration_months"
                          name="duration_months"
                          value={formData.duration_months}
                          onChange={handleInputChange}
                          placeholder="1"
                          min="1"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="price" className="form-label">
                          Price <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="price"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          placeholder="999.99"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="currency" className="form-label">
                          Currency <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          id="currency"
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="INR">INR (₹)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the plan features and benefits..."
                      rows="3"
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Roles <span className="text-danger">*</span>
                    </label>
                    <div className="row">
                      {roleOptions.map((role) => (
                        <div key={role} className="col-md-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`role_${role}`}
                              name="role_codes"
                              value={role}
                              checked={formData.role_codes.includes(role)}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`role_${role}`}
                            >
                              {role}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === "add" ? "Create Plan" : "Update Plan"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the subscription plan "
                  <strong>{deletingPlan?.name}</strong>"?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showModal || deleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default SubscriptionPlans;
